/**
 * MessageBubble — the rendering primitive for the Variant D chat
 * scroll. One ChatMessage in, one rendered row out.
 *
 * Design Spec Variant D §3.1 (bubble treatment revised 2026-07-14).
 * Three visual modes:
 *
 *   user       right-aligned FILLED violet bubble (brandColors.primary,
 *              white text), "You · HH:MM" label; a highlight-and-ask
 *              selection shows as a quote card above it
 *   assistant  left-aligned clean white hairline card
 *              (AsthraBubbleSurface)
 *   tool       full-width dashed strip, mono, status dot + meta
 *
 * SRP: pure presentation. Takes a single ChatMessage; renders one
 * row. No state, no side effects, no event handlers (callers wire
 * interactions externally if/when needed).
 *
 * Used in two places:
 *   1. Live EventLog rows in the day-grouped timeline stream
 *   2. Persisted-history rows paged in by scroll-up pagination
 *
 * The same component covers both — there is no "live" vs "archived"
 * variant. A message is a message regardless of its day group.
 *
 * # DECISION: Time format is local-timezone HH:MM (24-hour).
 * # WHY: matches the JSX prototype 1:1 and avoids AM/PM noise in a
 * #      compact 440px task pane. Writers are professionals reading
 * #      24-hour timestamps in trial logs all day; familiar register.
 * # DATE: 2026-04-26
 *
 * # DECISION: User messages render in a FILLED brand-tint bubble
 * #   (supersedes the 2026-04-26 "no bubble" decision).
 * # WHY: with the flat day-grouped timeline, bare right-aligned text
 * #      blended into system notes and assistant cards — roles were
 * #      indistinguishable at a glance. The light brand tint
 * #      (brandColors.light, white-label safe) with a mirrored
 * #      asymmetric radius gives the writer's voice its own surface
 * #      while the assistant keeps the white card + brand left border
 * #      as the agent signature. Label and aria contracts unchanged.
 * # DATE: 2026-07-14
 *
 * # DECISION: user bubble is a SOLID brand-primary fill with white text
 * #   (supersedes the light-tint fill above), assistant is a plain card.
 * # WHY: launch film screen 7 — violet user bubble, clean assistant
 * #      card. The solid fill makes "who asked" readable at a glance in
 * #      a long thread; brandColors.primary keeps the Indegene build
 * #      blue. White on #7454EC (Asthra) and on the Indegene blue both
 * #      clear 4.5:1 at 13px.
 * # DATE: 2026-10-14
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { memo, useState } from "react";
import ReactMarkdown, { type Components, defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
import { repairPartialTables } from "../../services/partialTables";
import { remarkSubSup } from "../../services/remarkSubSup";
import { handleResponseAction as executeResponseAction } from "../../services/responseActions";
import { intentLabel } from "../intentLabels";
import { findMentionTokens } from "../mentions";
import type { ChatMessage, QuotedMessage, UserChatMessage } from "../messageProjection";
import type { MentionAttachment } from "../../types";
import { brand } from "../../config/brand";
import { brandColors, chatSurface, fonts, inkColors, spacing, statusColors, surfaceColors, typeScale } from "../tokens";
import AsthraBubbleSurface from "./AsthraBubbleSurface";
import { CHANGE_LINK_PREFIX, ChangeChip, ChangeSetBar, linkifyChangeIds } from "./ChangeLinks";
import MentionMarker from "./MentionMarker";
import QuotedSelection from "./QuotedSelection";
import { summarizeAutoInstruction } from "../services/missingInfoProgress";
import { assistantMarkdownRules } from "./markdownStyles";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Raw character count above which text is collapsed behind "show more".
 *  Sized so ordinary prompts and replies never collapse — only long
 *  pastes do (was 50, which clamped even two-line messages). */
const TRUNCATE_LIMIT = 400;

/**
 * Render a sent user message, replacing each ``@[n]`` citation marker with an
 * asset chip (icon + truncated name) resolved from the message's attachments.
 *
 * Splits the text on the marker grammar (../mentions ``findMentionTokens``,
 * the same source of truth the composer backdrop uses) and interleaves the
 * plain-text runs unchanged. When a marker has no matching attachment — legacy
 * messages persisted before attachments were threaded, or a truncation slice
 * that clips a marker — the raw ``[n]`` is kept so nothing is lost.
 */
function renderUserText(text: string, attachments?: MentionAttachment[]): React.ReactNode {
  const tokens = findMentionTokens(text);
  if (tokens.length === 0) return text;
  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  tokens.forEach((token, i) => {
    if (token.start > cursor) nodes.push(text.slice(cursor, token.start));
    const att = attachments?.[token.number - 1];
    if (att) {
      nodes.push(
        <MentionMarker
          key={`m-${i}-${token.start}`}
          assetType={att.assetType}
          label={att.label}
          fileName={att.fileName}
        />,
      );
    } else {
      // No attachment for this marker — show the bare citation number.
      nodes.push(`[${token.number}]`);
    }
    cursor = token.end;
  });
  if (cursor < text.length) nodes.push(text.slice(cursor));
  return nodes;
}

/** Keep the chat's own ``asthra-change:`` links (react-markdown strips
 *  unknown protocols); everything else gets the default sanitising. */
function changeUrlTransform(url: string): string {
  return url.startsWith(CHANGE_LINK_PREFIX) ? url : defaultUrlTransform(url);
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface MessageBubbleProps {
  message: ChatMessage;
  /** When provided, a reply button appears on hover. Clicking calls
   *  this with the message so the caller can set a pending quote. */
  onReply?: (message: ChatMessage) => void;
  /** Fired after a bound proposal Insert completes (success or not),
   *  with a writer-readable outcome note. The shell records it as an
   *  ``approval`` EventLog entry so the decision is in the audit trail. */
  onProposalDecision?: (note: string, sectionId: string | null) => void;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  // -- USER ----------------------------------------------------------------
  userRowOuter: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "flex-start",
    gap: "4px",
  },
  userRow: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
  },
  userContent: {
    // 85% (assistant is 95%) guarantees a left gutter so the
    // right-alignment reads at a glance even on wide messages.
    maxWidth: "85%",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
  },
  // Label row — hover-gated Copy button left, "You · HH:MM" right.
  // Mirrors the assistant card's label row (actions beside timestamp).
  userLabelRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginBottom: "4px",
    paddingRight: "2px",
  },
  // Small uppercase mono meta — same register as the assistant label.
  userLabel: {
    fontSize: typeScale.label,
    color: inkColors.ink4,
    fontFamily: fonts.mono,
    letterSpacing: "0.6px",
    textTransform: "uppercase",
  },
  // Intent chip — the classified chat intent, pinned next to the
  // sender label so the routed action stays visible in the thread.
  intentChip: {
    fontSize: "8.5px",
    fontFamily: fonts.mono,
    fontWeight: 600,
    letterSpacing: "0.3px",
    textTransform: "uppercase",
    color: brandColors.mid,
    backgroundColor: brandColors.light,
    border: `1px solid ${brandColors.glow}`,
    borderRadius: "8px",
    padding: "0px 6px",
    lineHeight: "13px",
    whiteSpace: "nowrap",
    marginBottom: 0,
  },
  // Row that carries the persistent intent chip BELOW the user's message
  // bubble (right-aligned to match the user column).
  intentChipRow: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "4px",
  },
  // Decided edit-proposal footer — outcome line under the drafted
  // replacement ("Applied" / "Rejected").
  // The film's "issue resolved" state: a compact status pill under the
  // reply, green for applied, neutral for rejected.
  decisionFooter: {
    display: "inline-flex",
    alignItems: "center",
    marginTop: "8px",
    padding: "2px 8px",
    borderRadius: "6px",
    fontSize: "10.5px",
    fontFamily: fonts.mono,
    fontWeight: 600,
    letterSpacing: "0.3px",
    lineHeight: "16px",
  },
  decisionAccepted: {
    // Deeper green than statusColors.cool for text contrast; the tint
    // and border derive from the cool token.
    color: "var(--success)",
    background: `color-mix(in srgb, ${statusColors.cool} 12%, transparent)`,
    border: `1px solid color-mix(in srgb, ${statusColors.cool} 40%, transparent)`,
  },
  decisionRejected: {
    color: inkColors.ink3,
    background: surfaceColors.paperDim,
    border: `1px solid ${surfaceColors.line}`,
  },
  // The writer's speech surface — the film's solid violet bubble.
  // brandColors.primary (white-label safe) with white text; a soft
  // brand-tinted shadow lifts it off the paper background.
  userText: {
    background: brandColors.primary,
    border: `1px solid ${brandColors.primary}`,
    borderRadius: "10px",
    padding: "10px 14px",
    boxShadow: `0 2px 8px color-mix(in srgb, ${brandColors.primary} 20%, transparent)`,
    fontSize: typeScale.body,
    lineHeight: typeScale.bodyLineHeight,
    color: "var(--on-brand)",
    fontFamily: fonts.body,
    fontWeight: 500,
    textAlign: "left",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  // Highlight-and-ask: the Word selection the question was about, as a
  // quote card stacked above the bubble (QuotedSelection grammar —
  // the same card the composer showed before send).
  selectionQuote: {
    width: "100%",
    marginBottom: "6px",
  },

  // -- ASSISTANT -----------------------------------------------------------
  // Frame moved to ``AsthraBubbleSurface`` (Phase 5.1, 2026-04-26) —
  // see DECISION at the top of that file. We retain only layout
  // styles unique to the assistant variant: outer alignment / max-
  // width, inner padding + body text rules. The frame (white bg, 2px
  // brand-mid left, lineSoft 3 sides, asymmetric corners) lives in
  // one place now.
  assistantOuter: {
    alignSelf: "flex-start",
    maxWidth: "95%",
  },
  // Film card proportions: roomier padding than the old 8/12 bubble.
  assistantBubble: {
    padding: spacing.bubblePadding,
    fontSize: typeScale.body,
    wordBreak: "break-word",
  },
  // Shared with EditProposalMessage via markdownStyles.ts so the
  // proposal preview renders identically to a regular Asthra reply.
  assistantMarkdown: {
    ...assistantMarkdownRules,
  },

  // -- SYSTEM (activity) ---------------------------------------------------
  // Phase 8.1 inclusive-projection role. User and agent activity (reviews,
  // approvals, Word edits, fixes, loads) renders as one quiet left-aligned
  // line — dot, "You · 22:58" / "Asthra · 22:58", what happened — so the
  // sequence of work reads in order between the messages without competing
  // with them.
  systemRow: {
    alignSelf: "stretch",
    display: "flex",
    alignItems: "baseline",
    gap: "7px",
    padding: "2px 2px",
    fontSize: typeScale.meta,
    lineHeight: "17px",
  },
  activityDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    flexShrink: 0,
    alignSelf: "center",
    boxSizing: "border-box",
    background: inkColors.ink4,
  },
  activityDotYou: {
    background: brandColors.primary,
  },
  activityDotAgent: {
    background: "transparent",
    border: `1.5px solid ${brandColors.primary}`,
  },
  activityMeta: {
    flexShrink: 0,
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    letterSpacing: "0.2px",
    whiteSpace: "nowrap",
  },
  systemText: {
    flex: 1,
    minWidth: 0,
    color: inkColors.ink3,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },

  // -- TOOL ----------------------------------------------------------------
  toolRow: {
    alignSelf: "stretch",
    display: "flex",
    alignItems: "center",
    gap: "7px",
    padding: "5px 10px",
    background: chatSurface.paperWarmDim,
    border: `1px dashed ${surfaceColors.line}`,
    borderRadius: "3px",
    fontSize: typeScale.meta,
    fontFamily: fonts.mono,
    color: inkColors.ink3,
  },
  toolDot: {
    width: "5px",
    height: "5px",
    borderRadius: "50%",
    flexShrink: 0,
  },
  toolDotRunning: {
    background: chatSurface.toolRunning,
    animationName: {
      "0%": { opacity: 0.35 },
      "50%": { opacity: 1 },
      "100%": { opacity: 0.35 },
    },
    animationDuration: "1400ms",
    animationIterationCount: "infinite",
    animationTimingFunction: "ease-in-out",
    "@media (prefers-reduced-motion: reduce)": {
      animation: "none",
      opacity: 1,
    },
  },
  toolDotDone: {
    background: chatSurface.toolRunning,
    opacity: 0.55,
  },
  toolDotError: {
    background: "#E11D48", // statusColors.danger — explicit here to avoid extra import
  },
  toolName: {
    color: brandColors.primary,
    fontWeight: 700,
  },
  toolDivider: {
    color: inkColors.ink4,
  },
  toolSummary: {
    flex: 1,
    color: inkColors.ink2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  toolMeta: {
    color: inkColors.ink5,
  },

  // -- SHOW MORE -----------------------------------------------------------
  // Collapsed state for assistant (markdown) messages: visually clamps
  // the rendered output to 6 lines without altering the raw markdown
  // string. Merged onto ``assistantMarkdown`` when !expanded && isLong.
  assistantMarkdownClamped: {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 6,
    overflow: "hidden",
  },

  // Shared toggle button for user, assistant, and system roles.
  showMoreBtn: {
    display: "block",
    marginTop: "3px",
    fontSize: typeScale.label,
    fontFamily: fonts.mono,
    color: brandColors.primary,
    background: "none",
    border: "none",
    padding: "0",
    cursor: "pointer",
    lineHeight: 1.4,
    ":hover": { textDecoration: "underline" },
  },

  // Wrapper that lets the system-row centre both the text and the button.
  systemContent: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },

  // Message action buttons (Insert, Copy, Reply) — hover-gated,
  // rendered on the label row next to the "Asthra · HH:MM" timestamp
  // (AsthraBubbleSurface ``labelActions``), not inside the bubble.
  messageActionsBar: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "2px",
    opacity: 0,
    transition: "opacity 0.15s ease-out",
    pointerEvents: "none",
  },
  messageActionsBarVisible: {
    opacity: 1,
    pointerEvents: "auto",
  },
  // User message copy-only bar — hover-gated, inline on the label
  // row just before "You · HH:MM".
  userActionsBar: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    opacity: 0,
    transition: "opacity 0.15s ease-out",
    pointerEvents: "none",
  },
  userActionsBarVisible: {
    opacity: 1,
    pointerEvents: "auto",
  },
  actionBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    padding: "0",
    background: "var(--surface-raised)",
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    color: inkColors.ink2,
    transition: "all 0.15s ease-out",
    ":hover": {
      background: surfaceColors.paperDim,
      border: `1px solid ${brandColors.primary}`,
      color: brandColors.primary,
    },
    ":active": {
      background: brandColors.light,
    },
  },
  actionBtnLoading: {
    opacity: 0.6,
    cursor: "not-allowed",
    pointerEvents: "none",
  },
  actionBtnSuccess: {
    border: "1px solid #22c55e",
    color: "#22c55e",
  },
  // -- MESSAGE WRAPPER -------------------------------------------------------
  messageWrapper: {
    position: "relative" as const,
  },
  // -- QUOTED MESSAGE BLOCK ------------------------------------------------
  quotedBlock: {
    borderLeft: `2px solid ${brandColors.primary}`,
    background: brandColors.tint,
    borderRadius: "6px",
    padding: "4px 8px",
    marginBottom: "4px",
    fontSize: "11px",
    color: inkColors.ink3,
    fontStyle: "italic" as const,
  },
  quotedRole: {
    fontWeight: 600,
    fontStyle: "normal" as const,
    textTransform: "capitalize" as const,
    marginRight: "4px",
  },
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Render one chat message. Switches on ``message.role`` and renders
 * the matching variant. Total — every role is handled.
 *
 * Memoized (see export below) with a value comparator: the timeline
 * re-projects EventLog entries on every render, so ``message`` object
 * identity is fresh each time even when the content is unchanged. The
 * comparator does a shallow field comparison instead — appending to a
 * long timeline no longer re-renders every existing bubble. (The two
 * callbacks are useCallback-stable in the shell.)
 */
const MessageBubbleImpl: React.FC<MessageBubbleProps> = ({ message, onReply, onProposalDecision }) => {
  const styles = useStyles();
  const time = formatTime(message.at);
  const [expanded, setExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [actionLoading, setActionLoading] = useState<"copy" | "insert" | null>(null);
  const [actionSuccess, setActionSuccess] = useState<"copy" | "insert" | null>(null);

  const quotedBlock = (message as { quotedMessage?: QuotedMessage }).quotedMessage ? (
    <div className={styles.quotedBlock}>
      <span className={styles.quotedRole}>{(message as { quotedMessage: QuotedMessage }).quotedMessage.role}:</span>
      {(message as { quotedMessage: QuotedMessage }).quotedMessage.text.length > 80
        ? `${(message as { quotedMessage: QuotedMessage }).quotedMessage.text.slice(0, 80)}…`
        : (message as { quotedMessage: QuotedMessage }).quotedMessage.text}
    </div>
  ) : null;

  const handleAction = async (action: "copy" | "insert") => {
    setActionLoading(action);
    setActionSuccess(null);
    try {
      // Only assistant messages have text property
      const text = "text" in message ? message.text : "";
      if (!text) {
        throw new Error("No content to copy or insert");
      }
      // Bound proposal insert (ad-hoc draft): section-targeted Word
      // write + SSOT write-back, instead of insert-at-cursor.
      const insertTarget = action === "insert" && message.role === "assistant" ? message.insertTarget : undefined;
      if (insertTarget) {
        const { insertProposalIntoSection } = await import("../services/proposalInsert");
        const result = await insertProposalIntoSection(insertTarget, text);
        onProposalDecision?.(result.message, insertTarget.moduleId);
        if (!result.ok) {
          throw new Error(result.message);
        }
      } else {
        await executeResponseAction(action, text);
      }
      setActionSuccess(action);
      // Clear success state after 2 seconds
      setTimeout(() => setActionSuccess(null), 2000);
    } catch (err) {
      console.error(`[MessageBubble] ${action} action failed:`, err);
    } finally {
      setActionLoading(null);
    }
  };

  if (message.role === "user") {
    // Canned "Resolve with Asthra" instruction → short label (+ flag count
    // from the selection); the full instruction stays one click away.
    const autoSummary = summarizeAutoInstruction(message.text, (message as UserChatMessage).selectionContext);
    const isLong = autoSummary !== null || message.text.length > TRUNCATE_LIMIT;
    const displayText = autoSummary
      ? expanded
        ? message.text
        : autoSummary.label
      : !expanded && isLong
        ? `${message.text.slice(0, TRUNCATE_LIMIT)}\u2026`
        : message.text;
    return (
      <li
        className={mergeClasses(styles.userRowOuter, styles.messageWrapper)}
        aria-label={`You at ${time}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className={styles.userContent}>
          {quotedBlock}
          <div className={styles.userLabelRow}>
            {/* User messages only show Copy button (no Reply) — inline
                on the label row, just before "You · HH:MM". */}
            <div className={mergeClasses(styles.userActionsBar, isHovered && styles.userActionsBarVisible)}>
              <button
                type="button"
                className={`${styles.actionBtn} ${actionLoading === "copy" ? styles.actionBtnLoading : ""} ${actionSuccess === "copy" ? styles.actionBtnSuccess : ""}`}
                onClick={() => handleAction("copy")}
                disabled={actionLoading !== null}
                title="Copy"
                aria-label="Copy to clipboard"
              >
                {actionSuccess === "copy" ? (
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <title>Copied</title>
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 14 14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <title>Copy</title>
                    <path d="M2.43621 9.61724C1.64301 9.61724 1 8.97423 1 8.18103V2.64138C1 1.73487 1.73487 1 2.64138 1H8.18103C8.97423 1 9.61724 1.64301 9.61724 2.43621" />
                    <rect x="4.38281" y="4.38281" width="8.61719" height="8.61719" rx="1.64138" />
                  </svg>
                )}
              </button>
            </div>
            <span className={styles.userLabel}>{`You · ${time}`}</span>
          </div>
          {(() => {
            const sel = (message as UserChatMessage).selectionContext;
            return sel ? (
              <QuotedSelection
                text={sel}
                caption="Your selection"
                className={styles.selectionQuote}
                ariaLabel="Context used"
              />
            ) : null;
          })()}
          <div className={styles.userText}>{renderUserText(displayText, message.attachments)}</div>
          {message.intent ? (
            <div className={styles.intentChipRow}>
              <span className={styles.intentChip} aria-label={`Intent: ${intentLabel(message.intent)}`}>
                {intentLabel(message.intent)}
              </span>
            </div>
          ) : null}
          {isLong && (
            <button
              type="button"
              className={styles.showMoreBtn}
              onClick={() => setExpanded((e) => !e)}
              aria-expanded={expanded}
              aria-label={
                autoSummary
                  ? expanded
                    ? "Hide full instruction"
                    : "Show full instruction"
                  : expanded
                    ? "Show less"
                    : "Show full message"
              }
            >
              {autoSummary
                ? expanded
                  ? "hide full instruction"
                  : "show full instruction"
                : expanded
                  ? "show less"
                  : "show more"}
            </button>
          )}
        </div>
      </li>
    );
  }

  if (message.role === "assistant") {
    const isLong = message.text.length > TRUNCATE_LIMIT;
    // Decided edit-highlight proposal — outcome footer below the
    // drafted replacement; Insert hover action suppressed (the
    // decision already happened; re-inserting would double-apply).
    const editDecision = message.editDecision;
    // Agent edits this turn: C-id links become chips (jump to Word + diff).
    const changes = message.changes ?? [];
    const changeScope = String(message.at);
    const changeComponents: Components =
      changes.length === 0
        ? {}
        : {
            a: ({ href, children, node: _node, ...rest }) => {
              if (href?.startsWith(CHANGE_LINK_PREFIX)) {
                const change = changes.find((c) => c.id === href.slice(CHANGE_LINK_PREFIX.length));
                return change ? <ChangeChip change={change} scope={changeScope} label={children} /> : <span>{children}</span>;
              }
              return (
                <a href={href} {...rest}>
                  {children}
                </a>
              );
            },
          };
    // No intent chip here: the writer's message right above already carries
    // it, and a second "Edit highlight" chip on the reply read as noise.
    const bubbleLabel = `${brand.assistantName} · ${time}`;

    // Action buttons (hover-gated) — rendered beside the timestamp on
    // the label row, hidden for error messages.
    const actionsBar = !("isError" in message && (message as { isError?: boolean }).isError) && (
      <div className={mergeClasses(styles.messageActionsBar, isHovered && styles.messageActionsBarVisible)}>
        {/* Insert suppressed on decided edit proposals — the decision
            already happened; re-inserting would double-apply. */}
        {!editDecision && (
          <button
            type="button"
            className={`${styles.actionBtn} ${actionLoading === "insert" ? styles.actionBtnLoading : ""} ${actionSuccess === "insert" ? styles.actionBtnSuccess : ""}`}
            onClick={() => handleAction("insert")}
            disabled={actionLoading !== null}
            title="Insert into document"
            aria-label="Insert into Word document"
          >
            {actionSuccess === "insert" ? (
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <title>Inserted</title>
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <title>Insert into document</title>
                <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" />
                <path d="M12 11l0 6" />
                <path d="M9 14l6 0" />
              </svg>
            )}
          </button>
        )}
        <button
          type="button"
          className={`${styles.actionBtn} ${actionLoading === "copy" ? styles.actionBtnLoading : ""} ${actionSuccess === "copy" ? styles.actionBtnSuccess : ""}`}
          onClick={() => handleAction("copy")}
          disabled={actionLoading !== null}
          title="Copy"
          aria-label="Copy to clipboard"
        >
          {actionSuccess === "copy" ? (
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <title>Copied</title>
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : (
            <svg
              width="12"
              height="12"
              viewBox="0 0 14 14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <title>Copy</title>
              <path d="M2.43621 9.61724C1.64301 9.61724 1 8.97423 1 8.18103V2.64138C1 1.73487 1.73487 1 2.64138 1H8.18103C8.97423 1 9.61724 1.64301 9.61724 2.43621" />
              <rect x="4.38281" y="4.38281" width="8.61719" height="8.61719" rx="1.64138" />
            </svg>
          )}
        </button>
        {onReply && (
          <button
            type="button"
            className={styles.actionBtn}
            onClick={() => onReply(message)}
            title="Reply"
            aria-label="Reply to this message"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <title>Reply</title>
              <polyline points="9 17 4 12 9 7" />
              <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
            </svg>
          </button>
        )}
      </div>
    );

    return (
      <li
        className={styles.messageWrapper}
        style={{ display: "block", listStyle: "none" }}
        aria-label={`${brand.assistantName} at ${time}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <AsthraBubbleSurface
          label={bubbleLabel}
          labelActions={actionsBar || undefined}
          className={styles.assistantOuter}
          bubbleClassName={styles.assistantBubble}
          ariaLabel={`${brand.assistantName} at ${time}`}
        >
          {/* Response content */}
          <div
            className={`${styles.assistantMarkdown}${!expanded && isLong ? ` ${styles.assistantMarkdownClamped}` : ""}`}
          >
            <ReactMarkdown
              remarkPlugins={[[remarkGfm, { singleTilde: false }], remarkSubSup]}
              urlTransform={changeUrlTransform}
              components={changeComponents}
            >
              {changes.length > 0
                ? linkifyChangeIds(repairPartialTables(message.text), changes)
                : repairPartialTables(message.text)}
            </ReactMarkdown>
          </div>
          {changes.length > 0 && <ChangeSetBar changes={changes} scope={changeScope} />}
          {editDecision && (
            <div
              className={mergeClasses(
                styles.decisionFooter,
                editDecision === "accepted" ? styles.decisionAccepted : styles.decisionRejected,
              )}
              aria-label={editDecision === "accepted" ? "Applied to the document" : "Rejected — document unchanged"}
            >
              {editDecision === "accepted" ? "✓ Applied to the document" : "✗ Rejected — document unchanged"}
            </div>
          )}
          {isLong && (
            <button
              type="button"
              className={styles.showMoreBtn}
              onClick={() => setExpanded((e) => !e)}
              aria-expanded={expanded}
              aria-label={expanded ? "Show less" : "Show full message"}
            >
              {expanded ? "show less" : "show more"}
            </button>
          )}
        </AsthraBubbleSurface>
      </li>
    );
  }

  if (message.role === "system") {
    const isLong = message.text.length > TRUNCATE_LIMIT;
    const displayText = !expanded && isLong ? `${message.text.slice(0, TRUNCATE_LIMIT)}\u2026` : message.text;
    const actorName = message.actor === "you" ? "You" : message.actor === "asthra" ? brand.assistantName : null;
    return (
      <li
        className={mergeClasses(styles.systemRow, styles.messageWrapper)}
        aria-label={actorName ? `${actorName} at ${time}: ${message.text}` : `Activity at ${time}: ${message.text}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <span
          className={mergeClasses(
            styles.activityDot,
            message.actor === "you" && styles.activityDotYou,
            message.actor === "asthra" && styles.activityDotAgent,
          )}
          aria-hidden="true"
        />
        <span className={styles.activityMeta}>{actorName ? `${actorName} · ${time}` : time}</span>
        <div className={styles.systemContent}>
          <span className={styles.systemText}>{displayText}</span>
          {isLong && (
            <button
              type="button"
              className={styles.showMoreBtn}
              onClick={() => setExpanded((e) => !e)}
              aria-expanded={expanded}
              aria-label={expanded ? "Show less" : "Show full message"}
            >
              {expanded ? "show less" : "show more"}
            </button>
          )}
        </div>
      </li>
    );
  }

  // tool
  const dotClass = toolDotClass(message.status, styles);
  return (
    <li
      className={mergeClasses(styles.toolRow, styles.messageWrapper)}
      aria-label={`Tool ${message.name}: ${message.summary}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={`${styles.toolDot} ${dotClass}`} aria-hidden="true" />
      <span className={styles.toolName}>{message.name}</span>
      <span className={styles.toolDivider}>·</span>
      <span className={styles.toolSummary}>{message.summary}</span>
      {message.meta ? <span className={styles.toolMeta}>{message.meta}</span> : null}
    </li>
  );
};

/** Shallow own-key comparison across the ChatMessage union. Nested
 *  objects (e.g. a user message's ``quotedMessage``) compare by
 *  reference — a fresh nested object just means that one bubble
 *  re-renders, never a stale skip. */
function sameMessage(a: ChatMessage, b: ChatMessage): boolean {
  if (a === b) return true;
  const aRec = a as unknown as Record<string, unknown>;
  const bRec = b as unknown as Record<string, unknown>;
  const aKeys = Object.keys(aRec);
  if (aKeys.length !== Object.keys(bRec).length) return false;
  return aKeys.every((k) => aRec[k] === bRec[k]);
}

export const MessageBubble = memo(
  MessageBubbleImpl,
  (prev, next) =>
    sameMessage(prev.message, next.message) &&
    prev.onReply === next.onReply &&
    prev.onProposalDecision === next.onProposalDecision,
);

export default MessageBubble;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Format an epoch-ms timestamp as local-timezone "HH:MM" (24-hour).
 * Padded to 2 digits each.
 */
export function formatTime(epochMs: number): string {
  const d = new Date(epochMs);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

/** Pick the dot style for the tool message's current status. */
function toolDotClass(status: "running" | "done" | "error" | undefined, styles: ReturnType<typeof useStyles>): string {
  if (status === "running") return styles.toolDotRunning;
  if (status === "error") return styles.toolDotError;
  // ``done`` and undefined both render as the muted "completed" dot.
  return styles.toolDotDone;
}
